import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../lib/api';

// Severity → visual treatment. Picked to match the existing yellow
// sheet-note card and red error toasts elsewhere in the modal so the
// panel doesn't introduce a new color vocabulary.
const SEVERITY = {
  red: {
    bg: 'rgba(220, 38, 38, 0.08)',
    border: 'rgba(220, 38, 38, 0.35)',
    text: '#b91c1c',
    icon: 'fa-circle-exclamation',
    label: 'BLOCKER',
  },
  yellow: {
    bg: 'rgba(245, 158, 11, 0.08)',
    border: 'rgba(245, 158, 11, 0.4)',
    text: '#b45309',
    icon: 'fa-triangle-exclamation',
    label: 'CHECK',
  },
  blue: {
    bg: 'rgba(6, 182, 212, 0.08)',
    border: 'rgba(6, 182, 212, 0.35)',
    text: '#0e7490',
    icon: 'fa-circle-info',
    label: 'FYI',
  },
};

// Pillar 2 MVP — server runs deterministic rules over the schedule and
// returns findings; this panel surfaces them between the Sheet Note and
// the Schedule list in Review & Send. Click a finding to scroll the
// referenced event into view in the Schedule list (caller handles via
// the optional `onFocusEvent` callback).
export default function SheetCheckPanel({ sheetId, refreshKey, onFocusEvent }) {
  const [findings, setFindings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dismissed, setDismissed] = useState(() => new Set());
  // V1.5 opt-in: rule layer runs on every load (cheap), Claude layer
  // only fires when the admin clicks "Run AI check". Sticky for the
  // life of the modal so refresh-key bumps after edits include AI too.
  const [aiOn, setAiOn] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  const load = useCallback(async (withAi = aiOn) => {
    if (!sheetId) return;
    if (withAi) setAiLoading(true); else setLoading(true);
    setError(null);
    setAiError(null);
    try {
      // apiFetch already parses JSON and throws on non-2xx, so we get
      // back the body directly rather than a Response object.
      const path = withAi ? `/sheets/${sheetId}/check?ai=1` : `/sheets/${sheetId}/check`;
      const data = await apiFetch(path);
      setFindings(data.findings || []);
      if (data.aiError) setAiError(data.aiError);
    } catch (e) {
      setError(e.message || 'Failed to run check');
    } finally {
      setLoading(false);
      setAiLoading(false);
    }
  }, [sheetId, aiOn]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const runAi = async () => {
    setAiOn(true);
    await load(true);
  };

  const visible = (findings || []).filter(f => !dismissed.has(f.id));
  const hidden = (findings || []).length - visible.length;

  if (loading) {
    return (
      <div style={{ marginBottom: 18 }}>
        <Header />
        <div style={{ fontSize: 12, color: 'var(--text3)', fontStyle: 'italic', padding: '6px 0' }}>
          Running checks…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ marginBottom: 18 }}>
        <Header />
        <div style={{ fontSize: 12, color: '#b91c1c', padding: '6px 0' }}>
          Check failed: {error}. <button onClick={load} style={linkBtn}>retry</button>
        </div>
      </div>
    );
  }

  if (visible.length === 0) {
    return (
      <div style={{ marginBottom: 18 }}>
        <Header />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 10px',
            background: 'rgba(34, 197, 94, 0.08)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: 8,
            fontSize: 12,
            color: '#15803d',
          }}
        >
          <i className="fa-solid fa-circle-check" />
          <span>{hidden > 0 ? `All ${hidden} finding${hidden === 1 ? '' : 's'} dismissed.` : 'No issues detected on the schedule.'}</span>
        </div>
        <AiControls aiOn={aiOn} aiLoading={aiLoading} aiError={aiError} runAi={runAi} />
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 18 }}>
      <Header rightBadge={`${visible.length}`} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {visible.map(f => {
          const s = SEVERITY[f.severity] || SEVERITY.blue;
          const firstEventId = f.eventIds?.[0];
          return (
            <div
              key={f.id}
              style={{
                display: 'flex',
                gap: 9,
                padding: '8px 10px',
                background: s.bg,
                border: `1px solid ${s.border}`,
                borderRadius: 8,
                alignItems: 'flex-start',
              }}
            >
              <i
                className={`fa-solid ${s.icon}`}
                style={{ color: s.text, fontSize: 12, marginTop: 2, flexShrink: 0 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 2 }}>
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 800,
                      letterSpacing: 1.2,
                      color: s.text,
                    }}
                  >
                    {s.label}
                  </span>
                  {f.source === 'ai' && (
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: 0.8,
                        color: 'var(--text2)',
                        background: 'var(--bg3)',
                        border: '1px solid var(--border)',
                        borderRadius: 999,
                        padding: '0 5px',
                      }}
                      title="Suggested by Claude — judgment call, not a hard rule"
                    >
                      AI
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.4 }}>
                  {f.message}
                </div>
                {firstEventId && onFocusEvent && (
                  <button
                    onClick={() => onFocusEvent(firstEventId)}
                    style={{ ...linkBtn, color: s.text, marginTop: 4 }}
                  >
                    Jump to event →
                  </button>
                )}
              </div>
              <button
                onClick={() => setDismissed(prev => new Set([...prev, f.id]))}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text3)',
                  fontSize: 11,
                  padding: 2,
                  flexShrink: 0,
                }}
                title="Dismiss"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
          );
        })}
      </div>
      {hidden > 0 && (
        <button
          onClick={() => setDismissed(new Set())}
          style={{ ...linkBtn, marginTop: 6, fontSize: 11 }}
        >
          Restore {hidden} dismissed
        </button>
      )}
      <AiControls aiOn={aiOn} aiLoading={aiLoading} aiError={aiError} runAi={runAi} />
    </div>
  );
}

// Footer strip below the findings list. The AI layer is opt-in to keep
// Claude tokens off the hot path — admins click once, then it runs on
// every refresh-key bump for the rest of the modal session. Errors
// surface inline rather than throwing the whole panel into a failure
// state — the rule findings above are still useful.
function AiControls({ aiOn, aiLoading, aiError, runAi }) {
  if (aiLoading) {
    return (
      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text3)', fontStyle: 'italic' }}>
        <i className="fa-solid fa-wand-magic-sparkles" style={{ marginRight: 6 }} />
        Checking drive times + AI reasoning…
      </div>
    );
  }
  if (aiError) {
    return (
      <div style={{ marginTop: 8, fontSize: 11, color: '#b45309' }}>
        Deep check unavailable: {aiError}. <button onClick={runAi} style={linkBtn}>retry</button>
      </div>
    );
  }
  if (aiOn) {
    return (
      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text3)' }}>
        <i className="fa-solid fa-wand-magic-sparkles" style={{ marginRight: 6 }} />
        Drive times + AI included. <button onClick={runAi} style={linkBtn}>re-run</button>
      </div>
    );
  }
  return (
    <button
      onClick={runAi}
      style={{
        marginTop: 8,
        background: 'var(--bg3)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: '6px 10px',
        fontSize: 11,
        fontWeight: 600,
        color: 'var(--text2)',
        cursor: 'pointer',
        fontFamily: 'inherit',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}
      title="Run drive-time check (Distance Matrix) + Claude reasoning. Flags impossible transitions, international flight buffers, tight transit windows, etc."
    >
      <i className="fa-solid fa-wand-magic-sparkles" />
      Run deep check
    </button>
  );
}

function Header({ rightBadge }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: 6,
      }}
    >
      <p
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 1.5,
          color: 'var(--text3)',
          margin: 0,
          textTransform: 'uppercase',
        }}
      >
        <i className="fa-solid fa-clipboard-check" style={{ marginRight: 5, color: 'var(--blue)' }} />
        Sheet check
      </p>
      {rightBadge && (
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: 'var(--text2)',
            background: 'var(--bg3)',
            border: '1px solid var(--border)',
            borderRadius: 999,
            padding: '1px 7px',
          }}
        >
          {rightBadge}
        </span>
      )}
    </div>
  );
}

const linkBtn = {
  background: 'none',
  border: 'none',
  padding: 0,
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--blue)',
  cursor: 'pointer',
  fontFamily: 'inherit',
  textAlign: 'left',
};
